// Настройки → Анализ речи (Алексей 22.10 #1799 AI373, 27.10 #2985): галками — какие пункты чек-листа
// разговора оцениваем на этой точке и какие бары показывает сводка речи; свои бары — по любому пункту
// («Керамика») или апселл конкретной позиции («хот-дог»). Простой интерфейс: галки + «добавить бар».
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useAppState } from '../app/appContext';
import { useCan } from '@/hooks/useCan';
import { useT } from '@/contexts/I18nContext';
import { extractApiError } from '@/utils/apiError';
import { putSpeechSettings, useSpeechSettings, type SpeechCustomBar } from '@/api/speechSettings';
import { DEFAULT_OFF_ITEMS, STAGES } from '@/pages/Management/AnalyticsConversationsTab';

export default function SpeechSettings() {
  const { zone } = useAppState();
  const zoneId = zone.selectedZoneId;
  if (!zoneId) return <BranchRequiredState />;
  return <Body zoneId={zoneId} />;
}

function Body({ zoneId }: { zoneId: string }) {
  const { t } = useT();
  const canEdit = useCan('zone.config.edit', zoneId);
  const { data, isLoading, mutate } = useSpeechSettings(zoneId);
  const [items, setItems] = useState<Record<string, boolean>>({});
  const [hidden, setHidden] = useState<string[]>([]);
  const [custom, setCustom] = useState<SpeechCustomBar[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!data) return;
    setItems(data.items ?? {});
    setHidden(data.bars?.hidden ?? []);
    setCustom(data.bars?.custom ?? []);
  }, [data]);

  const label = (key: string) => t(`conversations.checklist.item.${key}`);
  const allItems = useMemo(() => STAGES.flatMap((st) => st.items.map((i) => ({ stage: st.stage, key: i.key as string }))), []);
  const dirty = !!data && JSON.stringify([items, hidden, custom]) !== JSON.stringify([data.items ?? {}, data.bars?.hidden ?? [], data.bars?.custom ?? []]);

  if (isLoading || !data) {
    return <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Загружаем…</div>;
  }
  const on = (key: string) => items[key] ?? !DEFAULT_OFF_ITEMS.has(key);
  const toggle = (key: string, v: boolean) => {
    setSaved(false);
    setItems((cur) => {
      const next = { ...cur };
      if (v === !DEFAULT_OFF_ITEMS.has(key)) delete next[key]; else next[key] = v;   // по умолчанию — без записи
      return next;
    });
  };

  async function save() {
    setBusy(true); setError(null);
    try {
      await putSpeechSettings(zoneId, { items, bars: { hidden, custom } });
      await mutate();
      setSaved(true);
    } catch (e) {
      setError(extractApiError(e, 'Не удалось сохранить'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card variant="section">
        <CardHeader>
          <CardTitle>Что оцениваем в разговоре</CardTitle>
          <CardDescription>Снимите галку — пункта не будет в карточке разговора на этой точке (например, керамики нет). Поставьте — «не было» станет серым крестом.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {STAGES.map((st) => (
            <div key={st.stage}>
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t(`conversations.stage.${st.stage}`)}</div>
              <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                {st.items.map((i) => {
                  const key = i.key as string;
                  return (
                    <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Switch checked={on(key)} disabled={!canEdit} onCheckedChange={(v) => toggle(key, v === true)} />
                      {label(key)}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card variant="section">
        <CardHeader>
          <CardTitle>Бары в сводке речи</CardTitle>
          <CardDescription>Какие бары видны наверху Данных → Речь и в Статусе. Свой бар — по любому пункту выше или по апселлу конкретной позиции.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-x-6 gap-y-1.5">
            {(data.standard_bars ?? []).map((b) => (
              <label key={b.id} className="flex cursor-pointer items-center gap-2 text-sm">
                <Switch checked={!hidden.includes(b.id)} disabled={!canEdit}
                  onCheckedChange={(v) => { setSaved(false); setHidden((h) => (v === true ? h.filter((x) => x !== b.id) : [...h, b.id])); }} />
                {b.title}
              </label>
            ))}
          </div>
          {custom.length > 0 && (
            <ul className="divide-y divide-border/60 rounded-lg border">
              {custom.map((c, idx) => (
                <li key={idx} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1 font-medium">{c.title}</span>
                  <span className="text-xs text-muted-foreground">{c.kind === 'flag' ? `пункт: ${label(c.field)}` : `апселл: «${c.match}»`}</span>
                  {canEdit && (
                    <Button variant="ghost" size="icon-sm" aria-label="Убрать бар" onClick={() => { setSaved(false); setCustom((cs) => cs.filter((_, j) => j !== idx)); }}>
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canEdit && <AddBar items={allItems.map((x) => x.key)} label={label} onAdd={(b) => { setSaved(false); setCustom((cs) => [...cs, b]); }} />}
        </CardContent>
      </Card>

      <div className="sticky bottom-3 z-10 flex items-center gap-3">
        <Button onClick={save} disabled={!canEdit || busy || !dirty}>
          {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
          Сохранить
        </Button>
        {saved && !dirty && <span className="text-sm text-muted-foreground">Сохранено</span>}
        {error && <span className="text-sm text-destructive">{error}</span>}
      </div>
    </div>
  );
}

function AddBar({ items, label, onAdd }: { items: string[]; label: (k: string) => string; onAdd: (b: SpeechCustomBar) => void }) {
  const [kind, setKind] = useState<'flag' | 'upsell_item'>('flag');
  const [field, setField] = useState('offered_ceramic');
  const [match, setMatch] = useState('');
  const [title, setTitle] = useState('');
  const auto = kind === 'flag' ? label(field) : match.trim() ? `Апселл: ${match.trim()}` : '';
  const ok = kind === 'flag' ? !!field : !!match.trim();
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/40 p-2">
      <Select wrapperClassName="w-auto" value={kind} onChange={(e) => setKind(e.target.value as 'flag' | 'upsell_item')}>
        <option value="flag">По пункту чек-листа</option>
        <option value="upsell_item">Апселл позиции</option>
      </Select>
      {kind === 'flag' ? (
        <Select wrapperClassName="w-auto" value={field} onChange={(e) => setField(e.target.value)}>
          {items.map((k) => <option key={k} value={k}>{label(k)}</option>)}
        </Select>
      ) : (
        <Input className="w-40" placeholder="хот-дог" value={match} onChange={(e) => setMatch(e.target.value)} />
      )}
      <Input className="w-48" placeholder={auto || 'Название бара'} value={title} onChange={(e) => setTitle(e.target.value)} />
      <Button size="sm" variant="outline" disabled={!ok}
        onClick={() => {
          const t = title.trim() || auto;
          onAdd(kind === 'flag' ? { kind, field, title: t } : { kind, match: match.trim(), title: t });
          setTitle(''); setMatch('');
        }}>
        <Plus className="mr-1 size-4" /> Добавить бар
      </Button>
    </div>
  );
}

